import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Phone,
  ShieldCheck,
  Wrench,
  Sparkles,
  LayoutGrid,
  Layers,
  Droplets,
  Hammer,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { COMPANY } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CANONICAL = "https://alldaypowerwashing.com/epoxy";

const COATINGS = [
  {
    id: "solid",
    name: "Solid Color",
    icon: LayoutGrid,
    desc: "Clean, single-tone finish. The classic durable garage floor. Hides imperfections, easy to clean, professional look.",
    bullets: ["UV-stable color options", "Easiest to keep clean", "Most cost-effective"],
  },
  {
    id: "metallic",
    name: "Metallic",
    icon: Sparkles,
    desc: "Pearlescent, marbled finish with depth and movement — looks like polished stone. Wow-factor for showrooms, man caves, retail.",
    bullets: ["Glass-like 3D depth", "Custom color blends", "Showroom appearance"],
  },
  {
    id: "flake",
    name: "Flake / Chip Broadcast",
    icon: Layers,
    desc: "Multi-color vinyl flake broadcast into the basecoat — the speckled, textured look. Hides everything, slip-resistant, super durable.",
    bullets: ["Best slip resistance", "Hides cracks & stains", "Most popular for garages"],
  },
];

const STALLS = ["1 car", "2 car", "3 car", "4+ car / commercial"];
const STALL_SQFT = {
  "1 car": 240,
  "2 car": 480,
  "3 car": 720,
  "4+ car / commercial": 1500,
};

// Per-square-foot rate [low, high] by property + coating
const EPOXY_RATES = {
  residential: {
    solid: [5, 6],
    flake: [6, 7],
    metallic: [7, 8],
    not_sure: [5, 8],
  },
  commercial: {
    solid: [4, 5],
    flake: [5, 6],
    metallic: [6, 8],
    not_sure: [4, 8],
  },
};
const MIN_JOB = 1500; // job-minimum floor

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

const SURFACE_CONDITIONS = [
  { id: "good", label: "Looks good — no major cracks" },
  { id: "minor_cracks", label: "Has some cracks or chips" },
  { id: "needs_grinding", label: "Stained / oily / needs heavy prep" },
  { id: "not_sure", label: "Not sure — you tell me" },
];

const REFERRAL_OPTIONS = [
  "Google search",
  "Google Maps",
  "Facebook",
  "Instagram",
  "YouTube",
  "Friend / word of mouth",
  "Saw our truck or yard sign",
  "Yelp / BBB",
  "Repeat customer",
  "Other",
];

const initialForm = {
  property_type: "residential",
  epoxy_coating_type: "",
  epoxy_stalls: "",
  epoxy_garage_sqft: "",
  epoxy_surface_condition: "",
  name: "",
  email: "",
  phone: "",
  address: "",
  message: "",
  referral_source: "",
};

export default function EpoxyPage() {
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const canSubmit = useMemo(
    () => form.name.trim() && form.phone.trim() && form.epoxy_stalls && form.epoxy_coating_type,
    [form]
  );

  // Live price range based on stalls (or sqft override) + coating + property type
  const priceRange = useMemo(() => {
    if (!form.epoxy_coating_type || (!form.epoxy_stalls && !form.epoxy_garage_sqft)) return null;
    const rates = EPOXY_RATES[form.property_type]?.[form.epoxy_coating_type];
    if (!rates) return null;
    const explicit = parseInt((form.epoxy_garage_sqft || "").replace(/[^\d]/g, ""), 10);
    const sqft = Number.isFinite(explicit) && explicit > 0
      ? explicit
      : STALL_SQFT[form.epoxy_stalls] || 0;
    if (!sqft) return null;
    const lo = Math.max(MIN_JOB, sqft * rates[0]);
    const hi = Math.max(MIN_JOB, sqft * rates[1]);
    return [Math.round(lo), Math.round(hi)];
  }, [form.epoxy_coating_type, form.epoxy_stalls, form.epoxy_garage_sqft, form.property_type]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit || loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        // Required generic fields so the lead saves cleanly
        service: "epoxy-floor-coating",
        size_tier: "medium",
        source: "epoxy_quote_form",
        estimated_price_min: priceRange ? priceRange[0] : null,
        estimated_price_max: priceRange ? priceRange[1] : null,
      });
      setSubmitted(true);
      toast.success("Got it. We'll be in touch within 24 hours.");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong. Please call or text 719-644-6105."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1726] text-white" data-testid="epoxy-page">
      <Helmet>
        <title>Epoxy Floor Coating — Residential &amp; Commercial Garages | All Day Power Washing</title>
        <meta
          name="description"
          content="Professional epoxy floor coatings for residential and commercial garages in Colorado Springs. Solid, metallic, and flake finishes. Veteran owned. Free quotes."
        />
        <link rel="canonical" href={CANONICAL} />
        <meta
          property="og:title"
          content="Epoxy Floor Coating — Garages | All Day Power Washing"
        />
        <meta property="og:url" content={CANONICAL} />
      </Helmet>

      <Header />

      {/* HERO */}
      <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1726] via-[#0F2540] to-[#0B1726]" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(135deg, #1E88E5 0 2px, transparent 2px 60px)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <Link
            to="/"
            data-testid="epoxy-back-home"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm font-heading uppercase tracking-wider mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-3 bg-[#E6B445] text-[#0B1726] font-heading font-black uppercase tracking-[0.25em] text-[11px] px-5 py-2.5 rounded-sm mb-6">
              <Layers className="w-3.5 h-3.5" /> Garage Epoxy Coating
            </div>
            <h1 className="font-heading text-5xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tight leading-[0.92]">
              Built to last.<br />
              <span className="text-[#E6B445]">Cleans like glass.</span>
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-slate-300 text-base sm:text-lg leading-relaxed">
              Professional epoxy floor coatings for residential and commercial
              garages across Colorado Springs and Denver. Three finishes — solid,
              metallic, and flake — installed by a veteran-owned crew that takes
              prep seriously.
            </p>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-slate-300 text-xs sm:text-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#1E88E5]" strokeWidth={1.5} />
                <span className="font-heading uppercase tracking-wider">Licensed &amp; Insured</span>
              </div>
              <div className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-[#1E88E5]" strokeWidth={1.5} />
                <span className="font-heading uppercase tracking-wider">Proper Surface Prep</span>
              </div>
              <div className="flex items-center gap-2">
                <Droplets className="w-4 h-4 text-[#1E88E5]" strokeWidth={1.5} />
                <span className="font-heading uppercase tracking-wider">Chemical &amp; Stain Resistant</span>
              </div>
            </div>

            <div className="mt-10">
              <a
                href="#epoxy-quote"
                data-testid="epoxy-hero-cta"
                className="inline-flex items-center gap-3 bg-[#1E88E5] hover:bg-[#42A5F5] text-white font-heading font-black tracking-[0.18em] uppercase text-base px-9 py-5 rounded-sm transition-all shadow-[0_15px_50px_-12px_rgba(30,136,229,0.7)]"
              >
                Get My Epoxy Quote
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* COATING TYPES */}
      <section className="relative py-20 sm:py-28 bg-[#0B1726]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#1E88E5]" />
              <span className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-[#1E88E5]">
                Three finishes
              </span>
              <span className="h-px w-10 bg-[#1E88E5]" />
            </div>
            <h2 className="font-heading text-4xl sm:text-6xl font-black uppercase tracking-tight text-white">
              Pick your <span className="text-[#E6B445]">look.</span>
            </h2>
            <p className="mt-4 text-slate-400 max-w-2xl mx-auto text-base sm:text-lg">
              Every floor we coat starts with the same heavy-duty prep. What changes is the finish.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {COATINGS.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                data-testid={`coating-card-${c.id}`}
                className="bg-[#142238] border border-slate-800 hover:border-[#1E88E5] p-7 rounded-md transition-colors flex flex-col h-full"
              >
                <div className="w-12 h-12 rounded-sm bg-[#1E88E5]/15 border border-[#1E88E5]/40 flex items-center justify-center mb-5">
                  <c.icon className="w-6 h-6 text-[#1E88E5]" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-3">
                  {c.name}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-5 flex-1">
                  {c.desc}
                </p>
                <ul className="space-y-2">
                  {c.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-[#1E88E5] mt-0.5 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PREP SECTION — the differentiator */}
      <section className="relative py-20 sm:py-28 bg-[#07111C] border-y border-slate-900">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-3 mb-5">
              <Hammer className="w-5 h-5 text-[#E6B445]" />
              <span className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-[#E6B445]">
                Why ours lasts
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-[1.02]">
              We spend more time <span className="text-[#1E88E5]">prepping</span> than installing.
            </h2>
            <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed">
              Epoxy is only as good as the surface underneath it. Most failures
              (peeling, bubbling, hot-tire pickup) come from skipped prep — not
              the coating itself. We won&apos;t roll a drop of product until the
              concrete is profiled, cleaned, and ready to bond.
            </p>
            <p className="mt-4 text-slate-400 text-sm sm:text-base leading-relaxed">
              That&apos;s why our floors keep their finish for years instead of
              months. Proper prep is non-negotiable on every job — residential
              garage or 30,000 sq ft warehouse.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                title: "Diamond grinding",
                body: "Open the concrete pores so the coating mechanically bonds — not just sticks on top.",
              },
              {
                title: "Crack & joint repair",
                body: "Fill cracks, chips, and control joints with structural filler. No surface defects telegraph through the finish.",
              },
              {
                title: "Oil & stain treatment",
                body: "Heavy-duty degreasing of every oil spot. Untreated stains will reject the epoxy and create fish-eye defects.",
              },
              {
                title: "Moisture & dust control",
                body: "Vacuum-shroud grinders capture 99% of the dust. Moisture-tested before any product touches the floor.",
              },
            ].map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                className="bg-[#142238] border border-slate-800 p-5 rounded-md"
              >
                <h3 className="font-heading text-base font-bold uppercase tracking-wider text-white mb-1">
                  {p.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING STRIP */}
      <section className="bg-[#0B1726] py-14 border-b border-slate-900">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <div className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-[#E6B445] mb-3">
            Starting At
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
            <div className="bg-[#142238] border border-slate-800 rounded-md p-7">
              <div className="font-heading text-sm font-bold tracking-wider uppercase text-slate-400 mb-2">
                Residential garage
              </div>
              <div className="font-heading text-4xl font-black text-white">
                $5–$8 <span className="text-base font-bold text-slate-400">/ sq ft</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Most 2-car garages run $2,400–$4,200 fully installed.
              </p>
            </div>
            <div className="bg-[#142238] border border-slate-800 rounded-md p-7">
              <div className="font-heading text-sm font-bold tracking-wider uppercase text-slate-400 mb-2">
                Commercial / showroom
              </div>
              <div className="font-heading text-4xl font-black text-white">
                $4–$8 <span className="text-base font-bold text-slate-400">/ sq ft</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Volume pricing for shops, warehouses, dealerships, and HOAs.
              </p>
            </div>
          </div>
          <p className="mt-5 text-xs text-slate-500 max-w-2xl mx-auto">
            Final price depends on coating type, surface condition, and total
            square footage. Heavy prep work, thicker top coats, or anti-slip
            additives may adjust the quote.
          </p>
        </div>
      </section>

      {/* DEDICATED EPOXY QUOTE FORM */}
      <section
        id="epoxy-quote"
        className="relative py-20 sm:py-28 bg-[#0B1726]"
        data-testid="epoxy-quote-section"
      >
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-10">
            <div className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-[#E6B445] mb-3">
              Get My Epoxy Quote
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-[1.02]">
              Tell us about <span className="text-[#1E88E5]">your floor.</span>
            </h2>
            <p className="mt-4 text-slate-400">
              90 seconds. We&apos;ll text or email a real quote within 24 hours.
            </p>
          </div>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="epoxy-success"
              className="bg-[#142238] border-2 border-[#1E88E5]/40 rounded-md p-10 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-[#1E88E5]/15 border border-[#1E88E5] flex items-center justify-center mx-auto mb-5">
                <Check className="w-8 h-8 text-[#1E88E5]" />
              </div>
              <h3 className="font-heading text-3xl font-black uppercase tracking-tight text-white mb-3">
                Got it.
              </h3>
              <p className="text-slate-300 max-w-md mx-auto">
                Thanks {form.name.split(" ")[0]}. We&apos;ve received your epoxy
                request and will reach out within 24 hours to confirm details
                and schedule a free on-site walkthrough.
              </p>
              <a
                href={COMPANY.phoneHref}
                className="inline-flex items-center gap-2 mt-7 text-[#E6B445] hover:text-white font-heading uppercase tracking-wider text-sm transition-colors"
              >
                <Phone className="w-4 h-4" />
                Need to talk now? {COMPANY.phone}
              </a>
            </motion.div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="epoxy-form"
              className="bg-[#142238] border border-slate-800 rounded-md p-6 sm:p-10 space-y-7"
            >
              {/* Property type */}
              <div>
                <Label>Property type</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {[
                    { id: "residential", label: "Residential" },
                    { id: "commercial", label: "Commercial" },
                  ].map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setForm({ ...form, property_type: p.id })}
                      data-testid={`epoxy-property-${p.id}`}
                      className={`px-4 py-3 rounded-sm border font-heading uppercase tracking-wider text-sm transition-all ${
                        form.property_type === p.id
                          ? "bg-[#1E88E5]/15 border-[#1E88E5] text-white"
                          : "bg-[#0B1726] border-slate-700 text-slate-400 hover:border-slate-500"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Coating type */}
              <div>
                <Label required>Coating preference</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                  {[
                    ...COATINGS.map((c) => ({ id: c.id, label: c.name })),
                    { id: "not_sure", label: "Not sure" },
                  ].map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setForm({ ...form, epoxy_coating_type: c.id })}
                      data-testid={`epoxy-coating-${c.id}`}
                      className={`px-3 py-3 rounded-sm border font-heading uppercase tracking-wider text-[11px] sm:text-xs transition-all ${
                        form.epoxy_coating_type === c.id
                          ? "bg-[#1E88E5]/15 border-[#1E88E5] text-white"
                          : "bg-[#0B1726] border-slate-700 text-slate-400 hover:border-slate-500"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stalls */}
              <div>
                <Label required>Garage size / # of stalls</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                  {STALLS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm({ ...form, epoxy_stalls: s })}
                      data-testid={`epoxy-stalls-${s.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                      className={`px-3 py-3 rounded-sm border font-heading uppercase tracking-wider text-[11px] sm:text-xs transition-all ${
                        form.epoxy_stalls === s
                          ? "bg-[#1E88E5]/15 border-[#1E88E5] text-white"
                          : "bg-[#0B1726] border-slate-700 text-slate-400 hover:border-slate-500"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Or enter square footage (optional)"
                  value={form.epoxy_garage_sqft}
                  onChange={(e) => setForm({ ...form, epoxy_garage_sqft: e.target.value })}
                  data-testid="epoxy-sqft-input"
                  className="mt-3 w-full bg-[#0B1726] border border-slate-800 rounded-sm px-4 py-3 text-white text-sm focus:border-[#1E88E5] focus:ring-1 focus:ring-[#1E88E5] outline-none transition-all"
                />
              </div>

              {/* Surface condition */}
              <div>
                <Label>Current surface condition</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {SURFACE_CONDITIONS.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setForm({ ...form, epoxy_surface_condition: c.id })}
                      data-testid={`epoxy-surface-${c.id}`}
                      className={`text-left px-3 py-3 rounded-sm border text-xs sm:text-sm transition-all ${
                        form.epoxy_surface_condition === c.id
                          ? "bg-[#1E88E5]/15 border-[#1E88E5] text-white"
                          : "bg-[#0B1726] border-slate-700 text-slate-400 hover:border-slate-500"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Live price range */}
              {priceRange && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="epoxy-price-range"
                  className="bg-[#1E88E5]/10 border border-[#1E88E5]/50 rounded-md p-5 sm:p-6"
                >
                  <div className="font-heading text-[11px] font-bold tracking-[0.3em] uppercase text-[#E6B445] mb-2">
                    Your estimated range
                  </div>
                  <div className="font-heading text-3xl sm:text-4xl font-black text-white">
                    {fmt(priceRange[0])} – {fmt(priceRange[1])}
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    Ballpark only. Final price confirmed after a free on-site walkthrough.
                  </div>
                </motion.div>
              )}

              {/* Contact info */}
              <div className="border-t border-slate-800 pt-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field
                    label="Full name"
                    required
                    value={form.name}
                    onChange={(v) => setForm({ ...form, name: v })}
                    testid="epoxy-name-input"
                  />
                  <Field
                    label="Phone"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                    testid="epoxy-phone-input"
                  />
                  <Field
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })}
                    testid="epoxy-email-input"
                  />
                  <Field
                    label="Property address"
                    value={form.address}
                    onChange={(v) => setForm({ ...form, address: v })}
                    testid="epoxy-address-input"
                  />
                </div>

                <div>
                  <Label>How did you hear about us?</Label>
                  <select
                    value={form.referral_source}
                    onChange={(e) => setForm({ ...form, referral_source: e.target.value })}
                    data-testid="epoxy-referral-select"
                    className="mt-2 w-full bg-[#0B1726] border border-slate-800 rounded-sm px-4 py-3 text-white text-sm focus:border-[#1E88E5] focus:ring-1 focus:ring-[#1E88E5] outline-none transition-all appearance-none cursor-pointer"
                  >
                    <option value="">Select an option…</option>
                    {REFERRAL_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Anything else? (optional)</Label>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="epoxy-message-input"
                    placeholder="Timeline, must-have colors, questions, etc."
                    className="mt-2 w-full bg-[#0B1726] border border-slate-800 rounded-sm px-4 py-3 text-white text-sm focus:border-[#1E88E5] focus:ring-1 focus:ring-[#1E88E5] outline-none transition-all resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={!canSubmit || loading}
                data-testid="epoxy-submit-btn"
                className="w-full bg-[#1E88E5] hover:bg-[#42A5F5] disabled:bg-slate-800 disabled:text-slate-500 text-white font-heading font-black tracking-[0.18em] uppercase text-base px-8 py-5 rounded-sm transition-all"
              >
                {loading ? "Sending…" : "Get My Epoxy Quote"}
              </button>
              <p className="text-center text-[11px] text-slate-500">
                No sales calls or spam. Free quote. Free walkthrough.
              </p>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ---------- inline form atoms ---------- */
const Label = ({ children, required }) => (
  <label className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-slate-400">
    {children} {required && <span className="text-[#E6B445]">*</span>}
  </label>
);

const Field = ({ label, value, onChange, type = "text", required = false, testid }) => (
  <div>
    <Label required={required}>{label}</Label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-2 w-full bg-[#0B1726] border border-slate-800 rounded-sm px-4 py-3 text-white text-sm focus:border-[#1E88E5] focus:ring-1 focus:ring-[#1E88E5] outline-none transition-all"
    />
  </div>
);
